import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { User } from '@/types'

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(loadUser())
  const token = ref<string | null>(localStorage.getItem('token'))

  const userName = computed(() => user.value?.name || '')
  const userRole = computed(() => {
    const role = String(user.value?.role || '').trim().toLowerCase()
    // Backend explicitly grants legacy teachers the student-admin capabilities.
    return role === 'teacher' ? 'student_admin' : role
  })
  const isLoggedIn = computed(() =>
    !!token.value && !!user.value &&
    ['student', 'student_admin', 'admin'].includes(userRole.value),
  )
  const isAdmin = computed(() => userRole.value === 'admin')
  const isStudentAdmin = computed(() => userRole.value === 'student_admin')

  function loadUser(): User | null {
    try {
      const raw = localStorage.getItem('user')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }

  function setAuth(newToken: string, newUser: User) {
    token.value = newToken
    user.value = newUser
    localStorage.setItem('token', newToken)
    localStorage.setItem('user', JSON.stringify(newUser))
  }

  function logout() {
    token.value = null
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }

  return {
    user,
    token,
    isLoggedIn,
    userName,
    userRole,
    isAdmin,
    isStudentAdmin,
    setAuth,
    logout,
  }
})
